import { withMainControls, withOverlayControls } from '@eb/eds-structure';
import {
    CONSUMER_CARD_DEFAULTS,
    EventCard,
    EventClient,
    EventClientProvider,
    FormattedEvent,
    ShareOptionsProps,
} from '@eb/event-renderer';
import { gettext } from '@eb/i18n';
import React from 'react';
import {
    label,
    shareType,
    trackShareAction,
} from '../../tracking/trackShareAction';
import { AffiliateCodes, GACodes } from '../../types';
import { ShareProps } from '../ShareEvent/ShareEventRoot';
import './CollectionsLink.scss';

type SingleCollectionLinkProps = {
    collectionName?: string;
    collectionUrl?: string;
};

type MultipleCollectionsLinkProps = {
    collectionsQuantity: number;
} & CollectionListProps &
    CollectionListModalProps;

type CollectionListProps = {
    shareComponentProps: ShareProps;
    collections?: FormattedEvent[];
};
type CollectionListModalProps = {
    showOverlay?: Function;
    hideOverlay?: Function;
};

type MultipleCollectionsProps = MultipleCollectionsLinkProps &
    CollectionListProps &
    CollectionListModalProps;

export type CollectionsLinkComponentProps = SingleCollectionLinkProps &
    MultipleCollectionsLinkProps;

const CollectionCardList = ({
    collections,
    shareComponentProps,
}: CollectionListProps): JSX.Element => (
    <div data-testid="creator-collections-card-list">
        {collections?.map((collection: FormattedEvent) => {
            const { id, name, url } = collection;
            const shareCollectionProps: ShareOptionsProps = {
                eventId: id,
                eventName: name,
                eventUrl: url,
                ...shareComponentProps,
            };

            return (
                <div
                    className="eds-g-cell eds-g-cell--has-overflow eds-g-cell-1-1 eds-g-cell-sw-6-12 eds-g-cell-md-4-12 collection__card-cell"
                    key={id}
                >
                    <EventCard
                        {...CONSUMER_CARD_DEFAULTS}
                        affCode={
                            AffiliateCodes.AFF_COLLECTIONS_FEATURING_THIS_EVENT
                        }
                        eventData={collection}
                        gaCategory={GACodes.GA_CATEGORY_CREATOR_COLLECTIONS}
                        isAuthenticated={true}
                        shareOptions={shareCollectionProps}
                        onDidShareClick={(id: shareType) =>
                            trackShareAction(
                                label(id),
                                GACodes.GA_CATEGORY_COLLECTION_CARD,
                            )
                        }
                        onWillShareClick={() =>
                            trackShareAction(
                                GACodes.GA_LABEL_OPEN_MODAL,
                                GACodes.GA_CATEGORY_COLLECTION_CARD,
                            )
                        }
                    />
                </div>
            );
        })}
    </div>
);

const eventClient = new EventClient();
export const CollectionsModalContent = ({
    collections,
    shareComponentProps,
}: CollectionListProps): JSX.Element => (
    <EventClientProvider client={eventClient}>
        <div className="collection-cards-bucket">
            <CollectionCardList {...{ collections, shareComponentProps }} />
        </div>
    </EventClientProvider>
);

const SingleCollection = ({
    collectionUrl,
    collectionName,
}: SingleCollectionLinkProps): JSX.Element => {
    const message = gettext(
        'Part of their <a href=%(collectionUrl)s target="_blank" rel="noopener noreferrer">%(collectionName)s</a> collection',
        { collectionUrl, collectionName },
    );
    const messageHtml = { __html: message.toString() };

    // eslint-disable-next-line react/no-danger
    return <div dangerouslySetInnerHTML={messageHtml} />;
};

const MultipleCollections = (props: MultipleCollectionsProps): JSX.Element => {
    const {
        showOverlay,
        hideOverlay,
        collectionsQuantity,
        collections,
        shareComponentProps,
    } = props;

    const _handleViewCollectionsClick = () => {
        if (showOverlay) {
            showOverlay('modal', {
                title: gettext('View collections'),
                type: 'complex',
                children: (
                    <CollectionsModalContent
                        collections={collections}
                        shareComponentProps={shareComponentProps}
                    />
                ),
                onClose: hideOverlay,
            });
        }
    };

    const MANY_COLLECTIONS_LIMIT = 9;
    const hasManyCollections = collectionsQuantity > MANY_COLLECTIONS_LIMIT;

    return (
        <>
            {gettext('Featured in ')}{' '}
            <button
                className="link-style"
                onClick={_handleViewCollectionsClick}
            >
                {hasManyCollections
                    ? gettext('%(MANY_COLLECTIONS_LIMIT)s+ collections', {
                          MANY_COLLECTIONS_LIMIT,
                      })
                    : gettext('%(collectionsQuantity)s collections', {
                          collectionsQuantity,
                      })}
            </button>
        </>
    );
};

const CollectionsLink = (props: CollectionsLinkComponentProps): JSX.Element => {
    const {
        collectionsQuantity,
        collectionName,
        collectionUrl,
        collections,
        shareComponentProps,
        showOverlay,
        hideOverlay,
    } = props;

    const hasCollections = collectionsQuantity > 0;

    const Component = () =>
        collectionsQuantity > 1 ? (
            <MultipleCollections
                {...{
                    showOverlay,
                    hideOverlay,
                    collectionsQuantity,
                    collections,
                    shareComponentProps,
                }}
            />
        ) : (
            <SingleCollection {...{ collectionName, collectionUrl }} />
        );

    return (
        <>
            {hasCollections ? (
                <div
                    className="eds-text-color--ui-600 eds-text-bm eds-l-mar-top-2"
                    data-testid="link-to-creator-collections"
                >
                    <Component />
                </div>
            ) : null}
        </>
    );
};

export default withMainControls(withOverlayControls(CollectionsLink));
